import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePartner } from "@/context/PartnerAuthContext";
import { partnerApi, errorText } from "@/lib/partnerApi";
import { P_BTN, P_FIELD, P_LABEL, P_EYEBROW } from "@/components/partner/PartnerUI";

function AuthFrame({ title, intro, children, testid }) {
    return (
        <div className="min-h-screen bg-[hsl(var(--cream))] flex flex-col" data-testid={testid}>
            <div className="max-w-md w-full mx-auto px-5 pt-10 pb-12 flex-1 flex flex-col">
                <div className="flex items-center gap-2">
                    <span className="logo-mark text-3xl text-[hsl(var(--forest))]">H<span className="dot">.</span></span>
                    <span className="font-display font-bold text-[hsl(var(--forest))]">O Homem da Obra</span>
                </div>
                <div className="mt-12">
                    <div className={P_EYEBROW}>Área de Parceiros</div>
                    <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[hsl(var(--forest))] leading-tight">{title}</h1>
                    {intro && <p className="mt-3 text-stone-600 text-base leading-relaxed">{intro}</p>}
                </div>
                <div className="mt-8">{children}</div>
            </div>
        </div>
    );
}

export function PartnerLogin() {
    const { partner, setPartner, refresh } = usePartner();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    if (partner) return <Navigate to={loc.state?.from || "/parceiros"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await partnerApi.post("/auth/login", { email, password });
            setPartner(data.partner);
            await refresh();
            nav(loc.state?.from || "/parceiros", { replace: true });
        } catch (err) {
            setError(errorText(err));
            setBusy(false);
        }
    };

    return (
        <AuthFrame title="Entrar" intro="Registe as atualizações das obras em que colabora." testid="partner-login-page">
            <form onSubmit={submit} className="space-y-4">
                <label className="block">
                    <span className={P_LABEL}>Email</span>
                    <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={P_FIELD} data-testid="partner-login-email" />
                </label>
                <label className="block">
                    <span className={P_LABEL}>Palavra-passe</span>
                    <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={P_FIELD} data-testid="partner-login-password" />
                </label>
                {error && <p className="text-sm text-red-700" role="alert" data-testid="partner-login-error">{error}</p>}
                <button type="submit" disabled={busy} className={`${P_BTN} !w-full`} data-testid="partner-login-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Entrar
                </button>
                <Link to="/parceiros/recuperar" className="block text-center py-3 text-[15px] font-semibold text-[hsl(var(--copper))]" data-testid="partner-forgot-link">
                    Esqueci-me da palavra-passe
                </Link>
            </form>
        </AuthFrame>
    );
}

export function PartnerForgot() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await partnerApi.post("/auth/forgot-password", { email });
            setDone(data.message);
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthFrame title="Recuperar palavra-passe" intro="Indique o seu email. Enviamos um link para definir uma nova palavra-passe." testid="partner-forgot-page">
            {done ? (
                <p className="rounded-2xl bg-emerald-50 text-emerald-900 p-4 text-[15px] leading-relaxed" data-testid="partner-forgot-done">{done}</p>
            ) : (
                <form onSubmit={submit} className="space-y-4">
                    <label className="block">
                        <span className={P_LABEL}>Email</span>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={P_FIELD} data-testid="partner-forgot-email" />
                    </label>
                    <button type="submit" disabled={busy} className={`${P_BTN} !w-full`} data-testid="partner-forgot-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Enviar link
                    </button>
                </form>
            )}
            <Link to="/parceiros/entrar" className="block text-center py-4 text-[15px] font-semibold text-[hsl(var(--copper))]" data-testid="partner-back-login">
                Voltar a entrar
            </Link>
        </AuthFrame>
    );
}

export function PartnerSetPassword() {
    const { setPartner, refresh } = usePartner();
    const nav = useNavigate();
    // O token sai da barra de endereço logo à entrada (não fica no histórico nem em estatísticas).
    const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
    const [info, setInfo] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        window.history.replaceState(null, "", window.location.pathname);
        if (!token) { setInfo(false); return; }
        partnerApi.get("/auth/token", { params: { token } }).then(({ data }) => setInfo(data)).catch(() => setInfo(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { setError("A palavra-passe deve ter pelo menos 8 caracteres."); return; }
        if (password !== confirm) { setError("As palavras-passe não coincidem."); return; }
        setBusy(true);
        setError("");
        try {
            const { data } = await partnerApi.post("/auth/set-password", { token, password });
            setPartner(data.partner);
            await refresh();
            toast.success("Palavra-passe definida.");
            nav("/parceiros", { replace: true });
        } catch (err) {
            setError(errorText(err));
            setBusy(false);
        }
    };

    if (info === null) return <AuthFrame title="A verificar o link…" testid="partner-setpw-page" />;
    if (!info) {
        return (
            <AuthFrame title="Link inválido" intro="Este link já não é válido ou já foi usado. Peça um novo link ou contacte O Homem da Obra." testid="partner-setpw-page">
                <Link to="/parceiros/recuperar" className={`${P_BTN} !w-full`} data-testid="partner-setpw-request-new">Pedir novo link</Link>
            </AuthFrame>
        );
    }
    const isInvite = info.purpose === "invite";
    return (
        <AuthFrame title={isInvite ? "Bem-vindo" : "Nova palavra-passe"} intro={`${isInvite ? "Defina a sua palavra-passe para entrar na Área de Parceiros." : "Defina uma nova palavra-passe."} Conta: ${info.email}`} testid="partner-setpw-page">
            <form onSubmit={submit} className="space-y-4">
                <label className="block">
                    <span className={P_LABEL}>Palavra-passe (mínimo 8 caracteres)</span>
                    <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={P_FIELD} data-testid="partner-setpw-password" />
                </label>
                <label className="block">
                    <span className={P_LABEL}>Repetir palavra-passe</span>
                    <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={P_FIELD} data-testid="partner-setpw-confirm" />
                </label>
                {error && <p className="text-sm text-red-700" role="alert" data-testid="partner-setpw-error">{error}</p>}
                <button type="submit" disabled={busy} className={`${P_BTN} !w-full`} data-testid="partner-setpw-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Guardar e entrar
                </button>
            </form>
        </AuthFrame>
    );
}
